// Polish Ministry of Finance VAT Whitelist API service.
// Works in both browser (FE) and Deno (edge functions).

import { cleanAccount, checkIbanChecksum } from './iban.ts';

export type WhiteListStatus = 'yes' | 'no' | 'na';

export interface BankAccountValidation {
  validIban: boolean;
  onWhiteList: WhiteListStatus;
}

export interface NipSubject {
  vatActive: boolean;
  accountNumbers: string[];
  subjectFound: boolean;
}

const DEFAULT_BASE_URL = 'https://wl-api.mf.gov.pl';

/** MF whitelist `/api/search/nip/...` response — only the fields we read. */
interface WhiteListSearchResponse {
  result?: {
    subject?: {
      statusVat?: string | null;
      accountNumbers?: string[] | null;
    } | null;
  } | null;
}

/** MF whitelist `/api/check/nip/.../bank-account/...` response — only the fields we read. */
interface WhiteListCheckResponse {
  result?: {
    accountAssigned?: string | null;
  } | null;
}

/** `Deno.env` as seen from a runtime-agnostic module (edge functions only). */
interface DenoEnvGlobal {
  Deno?: { env?: { get(key: string): string | undefined } };
}

export class CompanyValidator {

  // ── NIP ──────────────────────────────────────────────────────────────

  static normalizeNip(nip: string | null | undefined): string | null {
    if (!nip) return null;
    const cleaned = nip.replace(/[\s\-.]/g, '').replace(/^[A-Za-z]{2}/i, '');
    return cleaned || null;
  }

  static isValidNip(nip: string): boolean {
    if (!nip) return false;
    const cleaned = nip.replace(/[\s-]/g, '');
    const digits = cleaned.replace(/^[A-Za-z]{2}/, '');
    if (digits.length === 10 && /^\d+$/.test(digits)) return true;
    return digits.length >= 8 && digits.length <= 15;
  }

  // ── SWIFT ────────────────────────────────────────────────────────────

  static isValidSwift(swift: string): boolean {
    if (!swift) return false;
    return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/i.test(swift.trim());
  }

  // ── Whitelist lookup ─────────────────────────────────────────────────

  static async lookupNipSubject(nip: string): Promise<NipSubject | null> {
    try {
      const cleanNip = CompanyValidator.normalizeNip(nip);
      if (!cleanNip) return null;
      const base = CompanyValidator.getBaseUrl();
      const res = await fetch(`${base}/api/search/nip/${cleanNip}?date=${CompanyValidator.todayISO()}`);
      if (!res.ok) return null;
      const data = await res.json() as WhiteListSearchResponse | null;
      const subject = data?.result?.subject;
      if (!subject) return { vatActive: false, accountNumbers: [], subjectFound: false };
      return {
        vatActive: subject.statusVat === 'Czynny',
        accountNumbers: subject.accountNumbers ?? [],
        subjectFound: true,
      };
    } catch {
      return null;
    }
  }

  // ── Public API ───────────────────────────────────────────────────────

  /**
   * Validate a single bank account: IBAN check + Polish whitelist check.
   * Uses the /api/check/ endpoint which handles virtual/mask accounts server-side.
   * - validIban: format + checksum pass
   * - onWhiteList: 'yes' | 'no' | 'na' (non-Polish, API error, or invalid IBAN)
   */
  static async validateBankAccount(accountNumber: string, nip: string): Promise<BankAccountValidation> {
    const { cleaned, isPolish } = cleanAccount(accountNumber);
    const validIban = checkIbanChecksum(cleaned);
    if (!validIban || !isPolish) return { validIban, onWhiteList: 'na' };

    const onWhiteList = await CompanyValidator.checkAccountAssigned(cleaned, nip);
    return { validIban, onWhiteList };
  }

  /**
   * Validate multiple bank accounts against the same NIP.
   * Calls /api/check/ for each account individually (no batch endpoint).
   */
  private static async validateBankAccounts(accountNumbers: string[], nip: string): Promise<BankAccountValidation[]> {
    const results: BankAccountValidation[] = [];
    for (const accountNumber of accountNumbers) {
      results.push(await CompanyValidator.validateBankAccount(accountNumber, nip));
    }
    return results;
  }

  // ── Private helpers ──────────────────────────────────────────────────

  private static async checkAccountAssigned(cleanedIban: string, nip: string): Promise<WhiteListStatus> {
    try {
      const cleanNip = CompanyValidator.normalizeNip(nip);
      if (!cleanNip) return 'na';
      // The check endpoint expects 26-digit NRB (no country prefix)
      const nrb = cleanedIban.replace(/^PL/, '');
      const base = CompanyValidator.getBaseUrl();
      const res = await fetch(`${base}/api/check/nip/${cleanNip}/bank-account/${nrb}?date=${CompanyValidator.todayISO()}`);
      if (!res.ok) return 'na';
      const data = await res.json() as WhiteListCheckResponse | null;
      const assigned = data?.result?.accountAssigned;
      return assigned === 'TAK' ? 'yes' : assigned === 'NIE' ? 'no' : 'na';
    } catch {
      return 'na';
    }
  }

  private static getBaseUrl(): string {
    try {
      // Deno edge-function runtime: allow override via Deno.env (used in e2e to
      // point at the local VAT-whitelist mock instead of the live MF API).
      // Access via globalThis so the bare `Deno` global isn't referenced under tsc.
      const denoEnv = (globalThis as DenoEnvGlobal).Deno?.env;
      if (denoEnv?.get) {
        const denoOverride = denoEnv.get('VAT_WHITELIST_BASE_URL');
        if (denoOverride) return denoOverride;
      }
      const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
      return env?.VITE_MF_GOV_API_URL || DEFAULT_BASE_URL;
    } catch {
      return DEFAULT_BASE_URL;
    }
  }

  private static todayISO(): string {
    return new Date().toISOString().split('T')[0];
  }
}
